import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LineChart } from '../../components/charts';
import { toast } from 'react-hot-toast';
import { isDemoBrand } from '../../utils/demoBrand';
import { demoOverview, demoQueries, demoPages, demoBreakdown, demoMonthly } from './demoData';

// REACT_APP_API_URL is set to either the bare origin (https://api.glowytics.com)
// or the origin plus a trailing /api — strip the suffix so appending our own
// /api/... path cannot produce /api/api/....
const API_BASE = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return raw.replace(/\/+$/, '').replace(/\/api$/, '');
})();
const SC = `${API_BASE}/api/search-console`;

// ── formatting ──────────────────────────────────────────────────────────────

const nf = (n) => Number(n || 0).toLocaleString('tr-TR');
const pos = (v) => (v === null || v === undefined ? '—' : Number(v).toFixed(2));
const pct = (v) => `${(Number(v || 0) * 100).toFixed(2)}%`;

// A null delta means there was no baseline (the row did not exist in the
// comparison window), which is different from a 0% change.
function Delta({ value, invert = false, suffix = '%', digits = 1 }) {
  if (value === null || value === undefined) return <span className="text-gray-400">—</span>;
  const n = Number(value);
  const good = invert ? n < 0 : n > 0;
  const flat = Math.abs(n) < 0.05;
  const cls = flat ? 'text-gray-500' : good ? 'text-emerald-600' : 'text-red-600';
  const arrow = flat ? '' : n > 0 ? '↑' : '↓';
  return (
    <span className={`${cls} whitespace-nowrap`}>
      {n > 0 ? '+' : ''}{n.toFixed(digits)}{suffix} {arrow}
    </span>
  );
}

function ymdDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}
function todayYmd() {
  return new Date().toISOString().slice(0, 10);
}
function fmtMonthLabel(k) {
  if (!k || typeof k !== 'string') return k || '';
  const [y, m] = k.split('-').map(Number);
  if (!y || !m) return k;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('tr-TR', { month: 'short', year: 'numeric' });
}

// Constructed defensively: a throw at module scope would blank the whole page
// on browsers without Intl.DisplayNames, and country names are cosmetic.
const COUNTRY_NAMES = (() => {
  try { return new Intl.DisplayNames(['tr'], { type: 'region' }); }
  catch { return null; }
})();
// Search Console reports ISO-3166-1 alpha-3; Intl.DisplayNames wants alpha-2,
// so only the handful of codes worth naming are mapped and the rest show raw.
const A3_TO_A2 = {
  tur: 'TR', usa: 'US', deu: 'DE', gbr: 'GB', nld: 'NL', fra: 'FR', bel: 'BE', aut: 'AT',
  che: 'CH', ita: 'IT', esp: 'ES', rus: 'RU', ukr: 'UA', aze: 'AZ', irn: 'IR', irq: 'IQ',
  sau: 'SA', are: 'AE', bgr: 'BG', grc: 'GR', rou: 'RO', pol: 'PL', swe: 'SE', dnk: 'DK',
  nor: 'NO', can: 'CA', aus: 'AU', kaz: 'KZ', geo: 'GE', isr: 'IL', egy: 'EG', mar: 'MA'
};
function countryLabel(code) {
  const raw = String(code || '').toLowerCase();
  const a2 = A3_TO_A2[raw];
  if (!a2 || !COUNTRY_NAMES) return raw.toUpperCase();
  try { return `${COUNTRY_NAMES.of(a2)} (${raw.toUpperCase()})`; }
  catch { return raw.toUpperCase(); }
}

// ── generic building blocks ─────────────────────────────────────────────────

// Tables hold hundreds of rows; letting them all render inline made the page
// tens of screens tall. Every table body is capped at 15 visible rows and
// scrolls internally. 36px = p-2 padding (16) + text-sm line height (20); the
// extra row is the sticky header. Height is an inline style, not a Tailwind
// arbitrary value, because a template-built class name never reaches the JIT.
const ROW_H = 36;
const VISIBLE_ROWS = 15;
const scrollBoxStyle = { maxHeight: ROW_H * (VISIBLE_ROWS + 1) };

function Card({ title, subtitle, children, right }) {
  return (
    <div className="bg-white border rounded-xl p-4">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="text-sm font-semibold text-gray-800">{title}</div>
          {subtitle ? <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div> : null}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function ScoreRow({ title, subtitle, current, delta, loading, unavailable, unavailableNote }) {
  const cells = [
    // Query is a count of distinct ranking terms; its change is shown as an
    // absolute difference rather than a percentage.
    { label: 'Query', value: nf(current?.queries), d: delta?.queriesAbs, suffix: '', digits: 0 },
    { label: 'Clicks', value: nf(current?.clicks), d: delta?.clicks },
    { label: 'Impressions', value: nf(current?.impressions), d: delta?.impressions },
    { label: 'Ort. Pozisyon', value: pos(current?.position), d: delta?.position, invert: true, suffix: '', digits: 2 },
    { label: 'Tıklama Oranı', value: pct(current?.ctr), d: delta?.ctr }
  ];
  return (
    <div className="mb-4">
      <div className="flex items-baseline gap-2 mb-2">
        <h3 className="text-base font-semibold text-gray-800">{title}</h3>
        {subtitle ? <span className="text-xs text-gray-500">{subtitle}</span> : null}
      </div>
      {unavailable ? (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-3 text-sm">
          {unavailableNote}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {cells.map((c) => (
            <div key={c.label} className="bg-white border rounded-xl p-4 text-center">
              <div className="text-xs text-gray-500">{c.label}</div>
              <div className="text-2xl font-semibold mt-1 text-gray-900">
                {loading ? <span className="text-gray-300">…</span> : c.value}
              </div>
              <div className="text-xs mt-1">
                <Delta value={c.d} invert={c.invert} suffix={c.suffix ?? '%'} digits={c.digits ?? 1} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Server-paginated metric table. Rows are aggregated in Postgres, so the totals
 * shown are for the whole period rather than whatever slice fit in one response.
 */
function MetricTable({ title, subtitle, keyLabel, state, onPage, loading }) {
  const rows = state?.rows || [];
  const total = state?.total || 0;
  const page = state?.page || 1;
  const pageSize = state?.pageSize || 100;
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  return (
    <Card title={title} subtitle={subtitle}>
      <div className="overflow-x-auto overflow-y-auto" style={scrollBoxStyle}>
        <table className="min-w-full text-sm">
          {/* Sticky lives on the th, not the thead: with the default
              border-collapse a sticky thead does not stick in Chrome. */}
          <thead>
            <tr className="text-white">
              <th className="text-left p-2 font-medium w-8 sticky top-0 z-10 bg-[#b30000]">#</th>
              <th className="text-left p-2 font-medium sticky top-0 z-10 bg-[#b30000]">{keyLabel}</th>
              <th className="text-right p-2 font-medium sticky top-0 z-10 bg-[#b30000]">Impressions</th>
              <th className="text-right p-2 font-medium sticky top-0 z-10 bg-[#b30000]">% Δ</th>
              <th className="text-right p-2 font-medium sticky top-0 z-10 bg-[#b30000]">Clicks</th>
              <th className="text-right p-2 font-medium sticky top-0 z-10 bg-[#b30000]">% Δ</th>
              <th className="text-right p-2 font-medium sticky top-0 z-10 bg-[#b30000]">CTR</th>
              <th className="text-right p-2 font-medium sticky top-0 z-10 bg-[#b30000]">Poz.</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr><td colSpan={8} className="p-4 text-center text-gray-400">Yükleniyor…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="p-4 text-center text-gray-400">Veri yok</td></tr>
            ) : rows.map((r, i) => (
              <tr key={i} className="odd:bg-white even:bg-gray-50 border-b border-gray-100">
                <td className="p-2 text-gray-400">{from + i}.</td>
                <td className="p-2 max-w-[320px] truncate" title={r.query || r.page}>{r.query || r.page}</td>
                <td className="p-2 text-right tabular-nums">{nf(r.impressions)}</td>
                <td className="p-2 text-right"><Delta value={r.impressionsDelta} /></td>
                <td className="p-2 text-right tabular-nums">{nf(r.clicks)}</td>
                <td className="p-2 text-right"><Delta value={r.clicksDelta} /></td>
                <td className="p-2 text-right tabular-nums">{pct(r.ctr)}</td>
                <td className="p-2 text-right tabular-nums">{pos(r.position)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-end gap-3 mt-2 text-xs text-gray-600">
        <span>{from} - {to} / {nf(total)}</span>
        <button
          className="px-2 py-1 border rounded disabled:opacity-30 hover:bg-gray-50"
          disabled={page <= 1 || loading}
          onClick={() => onPage(page - 1)}
        >‹</button>
        <button
          className="px-2 py-1 border rounded disabled:opacity-30 hover:bg-gray-50"
          disabled={page >= lastPage || loading}
          onClick={() => onPage(page + 1)}
        >›</button>
      </div>
    </Card>
  );
}

/**
 * Keyword × month grid of average position. Wide by construction — one column
 * per month of retained history — so it scrolls in both directions with the
 * keyword column and the month header pinned.
 *
 * Position improves as it falls, so the month-over-month arrow is inverted:
 * moving from 9.9 to 6.6 is a gain.
 */
function MonthlyPositionTable({ state, loading, onPage, search, onSearch }) {
  const months = state?.months || [];
  const rows = state?.rows || [];
  const total = state?.total || 0;
  const page = state?.page || 1;
  const pageSize = state?.pageSize || 100;
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  return (
    <Card
      title="Non-Brand Query — Aylık Ortalama Pozisyon"
      subtitle="Tüm geçmiş · gösterim ağırlıklı ortalama · gösterim hacmine göre sıralı"
      right={
        <input
          className="border rounded px-2 py-1 text-xs w-48"
          placeholder="Query ara…"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
        />
      }
    >
      <div className="overflow-auto" style={scrollBoxStyle}>
        <table className="text-sm border-separate border-spacing-0">
          <thead>
            <tr>
              <th className="text-left p-2 font-medium sticky top-0 left-0 z-30 bg-[#b30000] text-white min-w-[260px] max-w-[260px]">
                Query
              </th>
              <th className="text-right p-2 font-medium sticky top-0 z-20 bg-[#b30000] text-white whitespace-nowrap">
                Toplam Gösterim
              </th>
              <th className="text-right p-2 font-medium sticky top-0 z-20 bg-[#b30000] text-white whitespace-nowrap">
                Ort. Poz.
              </th>
              {months.map(m => (
                <th key={m} className="text-right p-2 font-medium sticky top-0 z-20 bg-[#b30000] text-white whitespace-nowrap">
                  {fmtMonthLabel(m)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr><td colSpan={months.length + 3} className="p-4 text-center text-gray-400">Yükleniyor…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={months.length + 3} className="p-4 text-center text-gray-400">Veri yok</td></tr>
            ) : rows.map((r, i) => {
              const zebra = i % 2 ? 'bg-gray-50' : 'bg-white';
              return (
                <tr key={r.query} className={zebra}>
                  <td
                    className={`p-2 sticky left-0 z-10 ${zebra} border-b border-gray-100 min-w-[260px] max-w-[260px] truncate`}
                    title={r.query}
                  >
                    {from + i}. {r.query}
                  </td>
                  <td className="p-2 text-right tabular-nums border-b border-gray-100">{nf(r.impressions)}</td>
                  <td className="p-2 text-right tabular-nums border-b border-gray-100">{pos(r.position)}</td>
                  {months.map((m, mi) => {
                    const cell = r.byMonth?.[m];
                    // months run newest-first, so the previous month is the
                    // next column to the right.
                    const prev = r.byMonth?.[months[mi + 1]];
                    if (!cell || cell.position === null) {
                      return <td key={m} className="p-2 text-right text-gray-300 border-b border-gray-100">—</td>;
                    }
                    const diff = prev && prev.position !== null ? cell.position - prev.position : null;
                    const cls = diff === null || Math.abs(diff) < 0.05 ? 'text-gray-800'
                      : diff < 0 ? 'text-emerald-600' : 'text-red-600';
                    return (
                      <td
                        key={m}
                        className={`p-2 text-right tabular-nums whitespace-nowrap border-b border-gray-100 ${cls}`}
                        title={`${fmtMonthLabel(m)} · ${nf(cell.impressions)} gösterim · ${nf(cell.clicks)} tık`}
                      >
                        {cell.position.toFixed(1)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-end gap-3 mt-2 text-xs text-gray-600">
        <span>{from} - {to} / {nf(total)}</span>
        <button
          className="px-2 py-1 border rounded disabled:opacity-30 hover:bg-gray-50"
          disabled={page <= 1 || loading}
          onClick={() => onPage(page - 1)}
        >‹</button>
        <button
          className="px-2 py-1 border rounded disabled:opacity-30 hover:bg-gray-50"
          disabled={page >= lastPage || loading}
          onClick={() => onPage(page + 1)}
        >›</button>
      </div>
    </Card>
  );
}

// The origin is identical on every row and eats the width the path needs.
function pagePath(url) {
  try { const u = new URL(url); return (u.pathname + u.search) || '/'; }
  catch { return url; }
}

/**
 * Monthly performance of one URL section (the landing pages whose URL contains
 * a substring). Two tables: the section total per month, and a page × month
 * grid of the individual pages in it.
 */
function PageSectionMonthly({ state, loading, contains, onContains, metric, onMetric, onPage }) {
  const months = state?.months || [];
  const totals = state?.totals || [];
  const rows = state?.rows || [];
  const total = state?.total || 0;
  const page = state?.page || 1;
  const pageSize = state?.pageSize || 100;
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const metricLabel = metric === 'clicks' ? 'Tık' : 'Gösterim';

  return (
    <>
      <Card
        title={`Bölüm Performansı — URL'sinde "${contains}" geçen sayfalar`}
        subtitle="Tüm geçmiş · aylık toplam"
        right={
          <div className="flex items-center gap-2">
            <input
              className="border rounded px-2 py-1 text-xs w-40"
              placeholder="URL içinde geçen…"
              value={contains}
              onChange={(e) => onContains(e.target.value)}
            />
            <div className="flex text-xs border rounded overflow-hidden">
              {['impressions', 'clicks'].map(m => (
                <button
                  key={m}
                  className={`px-2 py-1 ${metric === m ? 'bg-[#b30000] text-white' : 'bg-white hover:bg-gray-50'}`}
                  onClick={() => onMetric(m)}
                >{m === 'clicks' ? 'Tık' : 'Gösterim'}</button>
              ))}
            </div>
          </div>
        }
      >
        <div className="overflow-x-auto overflow-y-auto" style={scrollBoxStyle}>
          <table className="min-w-full text-sm">
            <thead>
              <tr>
                {['Ay', 'Sayfa', 'Tık', '% Δ', 'Gösterim', '% Δ', 'CTR', 'Ort. Poz.'].map((h, i) => (
                  <th key={h} className={`p-2 sticky top-0 z-10 bg-gray-50 ${i === 0 ? 'text-left' : 'text-right'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && totals.length === 0 ? (
                <tr><td colSpan={8} className="p-4 text-center text-gray-400">Yükleniyor…</td></tr>
              ) : totals.length === 0 ? (
                <tr><td colSpan={8} className="p-4 text-center text-gray-400">Bu filtreyle eşleşen sayfa yok</td></tr>
              ) : totals.map((m, i) => (
                <tr key={m.month} className={i % 2 ? 'bg-gray-50' : 'bg-white'}>
                  <td className="p-2">{fmtMonthLabel(m.month)}</td>
                  <td className="p-2 text-right tabular-nums">{nf(m.pages)}</td>
                  <td className="p-2 text-right tabular-nums">{nf(m.clicks)}</td>
                  <td className="p-2 text-right"><Delta value={m.clicksDelta} /></td>
                  <td className="p-2 text-right tabular-nums">{nf(m.impressions)}</td>
                  <td className="p-2 text-right"><Delta value={m.impressionsDelta} /></td>
                  <td className="p-2 text-right tabular-nums">{pct(m.ctr)}</td>
                  <td className="p-2 text-right tabular-nums">{pos(m.position)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card
        title={`Sayfa Bazında Aylık ${metricLabel}`}
        subtitle={`URL'sinde "${contains}" geçen sayfalar · ${metricLabel.toLowerCase()} hacmine göre sıralı`}
      >
        <div className="overflow-auto" style={scrollBoxStyle}>
          <table className="text-sm border-separate border-spacing-0">
            <thead>
              <tr>
                <th className="text-left p-2 font-medium sticky top-0 left-0 z-30 bg-[#b30000] text-white min-w-[320px] max-w-[320px]">
                  Sayfa
                </th>
                <th className="text-right p-2 font-medium sticky top-0 z-20 bg-[#b30000] text-white whitespace-nowrap">
                  Toplam
                </th>
                {months.map(m => (
                  <th key={m} className="text-right p-2 font-medium sticky top-0 z-20 bg-[#b30000] text-white whitespace-nowrap">
                    {fmtMonthLabel(m)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr><td colSpan={months.length + 2} className="p-4 text-center text-gray-400">Yükleniyor…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={months.length + 2} className="p-4 text-center text-gray-400">Veri yok</td></tr>
              ) : rows.map((r, i) => {
                const zebra = i % 2 ? 'bg-gray-50' : 'bg-white';
                return (
                  <tr key={r.page} className={zebra}>
                    <td
                      className={`p-2 sticky left-0 z-10 ${zebra} border-b border-gray-100 min-w-[320px] max-w-[320px] truncate`}
                      title={r.page}
                    >
                      {from + i}. {pagePath(r.page)}
                    </td>
                    <td className="p-2 text-right tabular-nums font-medium border-b border-gray-100">{nf(r[metric])}</td>
                    {months.map(m => {
                      const cell = r.byMonth?.[m];
                      const v = cell ? cell[metric] : 0;
                      return (
                        <td
                          key={m}
                          className={`p-2 text-right tabular-nums whitespace-nowrap border-b border-gray-100 ${v ? '' : 'text-gray-300'}`}
                          title={cell ? `${fmtMonthLabel(m)} · ${nf(cell.impressions)} gösterim · ${nf(cell.clicks)} tık · poz. ${pos(cell.position)}` : undefined}
                        >
                          {v ? nf(v) : '—'}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-end gap-3 mt-2 text-xs text-gray-600">
          <span>{from} - {to} / {nf(total)} sayfa</span>
          <button
            className="px-2 py-1 border rounded disabled:opacity-30 hover:bg-gray-50"
            disabled={page <= 1 || loading}
            onClick={() => onPage(page - 1)}
          >‹</button>
          <button
            className="px-2 py-1 border rounded disabled:opacity-30 hover:bg-gray-50"
            disabled={page >= lastPage || loading}
            onClick={() => onPage(page + 1)}
          >›</button>
        </div>
      </Card>
    </>
  );
}

// ── page ────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 100;

export default function SearchConsoleOverview() {
  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;

  const headers = useMemo(
    () => ({ 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) }),
    [brandId]
  );

  // Filters
  const [range, setRange] = useState('30d');
  const [startDate, setStartDate] = useState(ymdDaysAgo(30));
  const [endDate, setEndDate] = useState(ymdDaysAgo(1));
  const [group, setGroup] = useState('ALL');       // ALL | BRAND | NON_BRAND
  const [country, setCountry] = useState('');
  const [device, setDevice] = useState('');

  // Data
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [countries, setCountries] = useState(null);
  const [devices, setDevices] = useState(null);

  const [brandQueriesMoM, setBrandQueriesMoM] = useState(null);
  const [brandQueriesYoY, setBrandQueriesYoY] = useState(null);
  const [nonBrandQueriesMoM, setNonBrandQueriesMoM] = useState(null);
  const [nonBrandQueriesYoY, setNonBrandQueriesYoY] = useState(null);
  const [brandPages, setBrandPages] = useState(null);
  const [nonBrandPages, setNonBrandPages] = useState(null);
  const [tablesLoading, setTablesLoading] = useState(false);

  // Keyword × month position grid. Like /monthly it covers the whole retained
  // history, so it does not follow the date filter above.
  const [monthlyPos, setMonthlyPos] = useState(null);
  const [monthlyPosLoading, setMonthlyPosLoading] = useState(false);
  const [monthlyPosSearch, setMonthlyPosSearch] = useState('');

  // URL-section performance (blog by default), also whole-history.
  const [section, setSection] = useState(null);
  const [sectionLoading, setSectionLoading] = useState(false);
  const [sectionContains, setSectionContains] = useState('blog');
  const [sectionMetric, setSectionMetric] = useState('impressions');

  // Brand-term editor
  const [termsDraft, setTermsDraft] = useState('');
  const [termsOpen, setTermsOpen] = useState(false);
  const [savingTerms, setSavingTerms] = useState(false);

  useEffect(() => {
    const today = new Date();
    const y = new Date(); y.setDate(today.getDate() - 1);
    const yesterday = y.toISOString().slice(0, 10);
    if (range === '7d') { setStartDate(ymdDaysAgo(7)); setEndDate(yesterday); }
    else if (range === '30d') { setStartDate(ymdDaysAgo(30)); setEndDate(yesterday); }
    else if (range === '90d') { setStartDate(ymdDaysAgo(90)); setEndDate(yesterday); }
    else if (range === 'thisMonth') {
      setStartDate(new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10));
      setEndDate(yesterday);
    } else if (range === 'lastMonth') {
      setStartDate(new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString().slice(0, 10));
      setEndDate(new Date(today.getFullYear(), today.getMonth(), 0).toISOString().slice(0, 10));
    }
  }, [range]);

  // Demo brands have no Search Console connection; they are served synthetic
  // data in the same shape so the page renders fully during presentations.
  const demo = isDemoBrand(brandId);

  const get = useCallback(async (path, params) => {
    if (demo) {
      switch (path) {
        case '/overview': return demoOverview(params);
        case '/queries': return demoQueries(params);
        case '/pages': return demoPages(params);
        case '/breakdown': return demoBreakdown(params);
        case '/monthly': return { rows: demoMonthly() };
        // No synthetic keyword×month grid; the card renders its empty state.
        case '/query-monthly-position': return { months: [], rows: [], total: 0 };
        case '/pages-monthly': return { months: [], rows: [], totals: [], total: 0 };
        default: return {};
      }
    }
    const qs = new URLSearchParams({ ...params, _t: Date.now() }).toString();
    const res = await fetch(`${SC}${path}?${qs}`, { credentials: 'include', headers });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || `${path} isteği başarısız`);
    return json;
  }, [headers, demo]);

  // Scorecards, trend, breakdowns
  useEffect(() => {
    if (!brandId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const base = { startDate, endDate, group, country, device };
        const [ov, cs, ds] = await Promise.all([
          get('/overview', base),
          get('/breakdown', { startDate, endDate, dimension: 'country', limit: 50 }),
          get('/breakdown', { startDate, endDate, dimension: 'device', limit: 10 })
        ]);
        if (cancelled) return;
        setOverview(ov);
        setCountries(cs);
        setDevices(ds);
        setTermsDraft((ov.brandTerms || []).join(', '));
      } catch (e) {
        if (!cancelled) toast.error(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [brandId, startDate, endDate, group, country, device, get]);

  // Monthly history is independent of the selected range.
  useEffect(() => {
    if (!brandId) return;
    get('/monthly', {}).then(r => setMonthly(r.rows || [])).catch(() => {});
  }, [brandId, get]);

  const loadMonthlyPos = useCallback(async (nextPage = 1, searchTerm = '') => {
    if (!brandId) return;
    setMonthlyPosLoading(true);
    try {
      const r = await get('/query-monthly-position', {
        group: 'NON_BRAND',
        pageSize: PAGE_SIZE,
        page: nextPage,
        ...(searchTerm ? { search: searchTerm } : {})
      });
      setMonthlyPos(r);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setMonthlyPosLoading(false);
    }
  }, [brandId, get]);

  // Debounced so typing in the search box does not fire a scan per keystroke.
  useEffect(() => {
    const t = setTimeout(() => loadMonthlyPos(1, monthlyPosSearch), monthlyPosSearch ? 400 : 0);
    return () => clearTimeout(t);
  }, [loadMonthlyPos, monthlyPosSearch]);

  const loadSection = useCallback(async (nextPage = 1, contains = 'blog', metric = 'impressions') => {
    if (!brandId || !contains.trim()) { setSection(null); return; }
    setSectionLoading(true);
    try {
      setSection(await get('/pages-monthly', {
        contains: contains.trim(), metric, pageSize: PAGE_SIZE, page: nextPage
      }));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSectionLoading(false);
    }
  }, [brandId, get]);

  useEffect(() => {
    const t = setTimeout(() => loadSection(1, sectionContains, sectionMetric), 400);
    return () => clearTimeout(t);
  }, [loadSection, sectionContains, sectionMetric]);

  const loadTables = useCallback(async (pages = {}) => {
    if (!brandId) return;
    setTablesLoading(true);
    try {
      const base = { startDate, endDate, pageSize: PAGE_SIZE };
      const [bqM, bqY, nqM, nqY, bp, nbp] = await Promise.all([
        get('/queries', { ...base, group: 'BRAND', compare: 'prev', page: pages.bqM || 1 }),
        get('/queries', { ...base, group: 'BRAND', compare: 'yoy', page: pages.bqY || 1 }),
        get('/queries', { ...base, group: 'NON_BRAND', compare: 'prev', page: pages.nqM || 1 }),
        get('/queries', { ...base, group: 'NON_BRAND', compare: 'yoy', page: pages.nqY || 1 }),
        get('/pages', { ...base, group: 'BRAND', compare: 'prev', page: pages.bp || 1 }),
        get('/pages', { ...base, group: 'NON_BRAND', compare: 'prev', page: pages.nbp || 1 })
      ]);
      setBrandQueriesMoM(bqM); setBrandQueriesYoY(bqY);
      setNonBrandQueriesMoM(nqM); setNonBrandQueriesYoY(nqY);
      setBrandPages(bp); setNonBrandPages(nbp);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setTablesLoading(false);
    }
  }, [brandId, startDate, endDate, get]);

  useEffect(() => { loadTables(); }, [loadTables]);

  // Paging one table re-requests only that table.
  const pageTable = useCallback(async (kind, setter, params, nextPage) => {
    try {
      setTablesLoading(true);
      const r = await get(kind, { startDate, endDate, pageSize: PAGE_SIZE, ...params, page: nextPage });
      setter(r);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setTablesLoading(false);
    }
  }, [get, startDate, endDate]);

  const saveTerms = async () => {
    if (demo) { toast.error('Demo markada marka terimleri değiştirilemez'); return; }
    setSavingTerms(true);
    try {
      const terms = termsDraft.split(',').map(s => s.trim()).filter(Boolean);
      const res = await fetch(`${SC}/brand-terms`, {
        method: 'PUT', credentials: 'include', headers, body: JSON.stringify({ terms })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Kaydedilemedi');
      toast.success('Marka terimleri kaydedildi');
      setTermsOpen(false);
      // Every brand-split figure on the page depends on these terms.
      const base = { startDate, endDate, group, country, device };
      setOverview(await get('/overview', base));
      loadTables();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingTerms(false);
    }
  };

  const chartData = useMemo(() => {
    const daily = overview?.daily || [];
    return {
      labels: daily.map(d => d.date),
      datasets: [
        {
          label: 'Clicks', data: daily.map(d => d.clicks), yAxisID: 'y',
          borderColor: 'rgb(139, 0, 0)', backgroundColor: 'rgba(139,0,0,0.08)',
          borderWidth: 2, pointRadius: 2, tension: 0.3
        },
        {
          label: 'Impressions', data: daily.map(d => d.impressions), yAxisID: 'y1',
          borderColor: 'rgb(240, 128, 128)', backgroundColor: 'rgba(240,128,128,0.08)',
          borderWidth: 2, pointRadius: 2, tension: 0.3
        },
        {
          label: 'Average Position', data: daily.map(d => d.position), yAxisID: 'y2',
          borderColor: 'rgb(233, 30, 99)', backgroundColor: 'rgba(233,30,99,0.08)',
          borderWidth: 2, pointRadius: 2, tension: 0.3
        }
      ]
    };
  }, [overview]);

  const chartOptions = useMemo(() => ({
    interaction: { mode: 'index', intersect: false },
    plugins: { legend: { display: true, position: 'top' } },
    scales: {
      y: { type: 'linear', position: 'left', beginAtZero: true, title: { display: true, text: 'Clicks' } },
      y1: { type: 'linear', position: 'right', beginAtZero: true, grid: { drawOnChartArea: false }, title: { display: true, text: 'Impressions' } },
      // Position is better when lower, so the axis is reversed and kept off to
      // the side rather than sharing a scale with the volume metrics.
      y2: { type: 'linear', position: 'right', reverse: true, grid: { drawOnChartArea: false }, display: false }
    }
  }), []);

  const rangeLabel = `${startDate} → ${endDate}`;
  const yoyUnavailable = overview?.priorYear && overview.priorYear.hasData === false;
  const cov = overview?.dataCoverage;

  if (!brandId) {
    return <div className="p-6 text-gray-600">Lütfen önce bir marka seçin.</div>;
  }

  return (
    <div className="p-6 space-y-4">
      {/* Header + filters */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <img src="/logo_search_consolev44.svg" alt="Search Console" className="h-12 md:h-14 w-auto" />
        <div className="flex flex-wrap items-center gap-2">
          {['7d', '30d', '90d', 'thisMonth', 'lastMonth'].map(r => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium ${range === r ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}`}
            >
              {r === '7d' ? '7 Gün' : r === '30d' ? '30 Gün' : r === '90d' ? '90 Gün' : r === 'thisMonth' ? 'Bu Ay' : 'Geçen Ay'}
            </button>
          ))}
          <input type="date" value={startDate} max={todayYmd()}
            onChange={(e) => { setRange('custom'); setStartDate(e.target.value); }}
            className="border rounded-md px-2 py-1 text-sm" />
          <span className="text-gray-400 text-sm">→</span>
          <input type="date" value={endDate} max={todayYmd()}
            onChange={(e) => { setRange('custom'); setEndDate(e.target.value); }}
            className="border rounded-md px-2 py-1 text-sm" />
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 bg-white border rounded-xl p-3">
        <label className="text-sm">
          <div className="text-xs text-gray-500 mb-1">Brand / Non-Brand</div>
          <select value={group} onChange={e => setGroup(e.target.value)} className="border rounded-md px-2 py-1.5 text-sm min-w-[160px]">
            <option value="ALL">Tümü</option>
            <option value="BRAND">Brand</option>
            <option value="NON_BRAND">Non-Brand</option>
          </select>
        </label>
        <label className="text-sm">
          <div className="text-xs text-gray-500 mb-1">Country</div>
          <select value={country} onChange={e => { setCountry(e.target.value); if (e.target.value) setDevice(''); }}
            className="border rounded-md px-2 py-1.5 text-sm min-w-[200px]">
            <option value="">Tümü</option>
            {(countries?.rows || []).map(c => (
              <option key={c.key} value={c.key}>{countryLabel(c.key)}</option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <div className="text-xs text-gray-500 mb-1">Device Category</div>
          <select value={device} onChange={e => { setDevice(e.target.value); if (e.target.value) setCountry(''); }}
            className="border rounded-md px-2 py-1.5 text-sm min-w-[160px]">
            <option value="">Tümü</option>
            {(devices?.rows || []).map(d => (
              <option key={d.key} value={d.key}>{d.key}</option>
            ))}
          </select>
        </label>
        <div className="flex-1" />
        <div className="text-xs text-gray-500">
          <div>
            Marka terimleri:{' '}
            <span className="font-medium text-gray-700">
              {(overview?.brandTerms || []).join(', ') || '—'}
            </span>
            {overview?.brandTermsAuto ? <span className="ml-1 text-amber-600">(otomatik)</span> : null}
            <button onClick={() => setTermsOpen(v => !v)} className="ml-2 text-blue-600 hover:underline">düzenle</button>
          </div>
          {cov?.minDate ? <div className="mt-1">Veri aralığı: {cov.minDate} → {cov.maxDate}</div> : null}
        </div>
      </div>

      {termsOpen ? (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
          <div className="text-sm text-gray-700 mb-2">
            Bir sorgu, bu terimlerden herhangi birini içeriyorsa <b>Brand</b> sayılır (büyük/küçük harf duyarsız, virgülle ayırın).
            Boş bırakılırsa en çok tıklanan sorgu otomatik marka kabul edilir.
          </div>
          <div className="flex gap-2">
            <input value={termsDraft} onChange={e => setTermsDraft(e.target.value)}
              placeholder="ör. euro, europcar"
              className="flex-1 border rounded-md px-3 py-1.5 text-sm" />
            <button onClick={saveTerms} disabled={savingTerms}
              className="px-3 py-1.5 rounded-md text-sm font-medium bg-blue-600 text-white disabled:opacity-50">
              {savingTerms ? 'Kaydediliyor…' : 'Kaydet'}
            </button>
            <button onClick={() => setTermsOpen(false)} className="px-3 py-1.5 rounded-md text-sm border">Vazgeç</button>
          </div>
        </div>
      ) : null}

      {/* Trend */}
      <Card
        title="Clicks · Impressions · Average Position"
        subtitle={rangeLabel}
        right={loading ? <span className="text-xs text-gray-400">Yükleniyor…</span> : null}
      >
        <LineChart data={chartData} options={chartOptions} height={300} />
      </Card>

      {/* Scorecards */}
      <ScoreRow
        title="Geçen Ay Karşılaştırmalı"
        subtitle={overview?.previous?.range ? `${overview.previous.range.start} → ${overview.previous.range.end}` : ''}
        current={overview?.current}
        delta={overview?.previous?.delta}
        loading={loading}
      />
      <ScoreRow
        title="Geçen Yıl Karşılaştırmalı"
        subtitle={overview?.priorYear?.range ? `${overview.priorYear.range.start} → ${overview.priorYear.range.end}` : ''}
        current={overview?.current}
        delta={overview?.priorYear?.delta}
        loading={loading}
        unavailable={yoyUnavailable}
        unavailableNote={`Bu marka için ${overview?.priorYear?.range?.start} tarihli veri yok (en eski kayıt: ${cov?.minDate || '—'}). Geçmiş veri çekildiğinde bu kartlar otomatik dolar.`}
      />

      {/* Landing pages */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        <MetricTable
          title="Brand Landing Page Performansları"
          subtitle="Bir Önceki Ay Kıyaslamalı"
          keyLabel="Landing Page"
          state={brandPages}
          loading={tablesLoading}
          onPage={(p) => pageTable('/pages', setBrandPages, { group: 'BRAND', compare: 'prev' }, p)}
        />
        <MetricTable
          title="Non-Brand Landing Page Performansları"
          subtitle="Bir Önceki Ay Kıyaslamalı"
          keyLabel="Açılış Sayfası"
          state={nonBrandPages}
          loading={tablesLoading}
          onPage={(p) => pageTable('/pages', setNonBrandPages, { group: 'NON_BRAND', compare: 'prev' }, p)}
        />
      </div>

      {/* Brand queries */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        <MetricTable
          title="Brand Query Performansları"
          subtitle="Bir Önceki Ay Kıyaslamalı"
          keyLabel="Query"
          state={brandQueriesMoM}
          loading={tablesLoading}
          onPage={(p) => pageTable('/queries', setBrandQueriesMoM, { group: 'BRAND', compare: 'prev' }, p)}
        />
        <MetricTable
          title="Brand Query Performansları"
          subtitle="Önceki Yıl Kıyaslamalı"
          keyLabel="Query"
          state={brandQueriesYoY}
          loading={tablesLoading}
          onPage={(p) => pageTable('/queries', setBrandQueriesYoY, { group: 'BRAND', compare: 'yoy' }, p)}
        />
      </div>

      {/* Non-brand queries */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        <MetricTable
          title="Non-Brand Query Performansları"
          subtitle="Bir Önceki Ay Kıyaslamalı"
          keyLabel="Query"
          state={nonBrandQueriesMoM}
          loading={tablesLoading}
          onPage={(p) => pageTable('/queries', setNonBrandQueriesMoM, { group: 'NON_BRAND', compare: 'prev' }, p)}
        />
        <MetricTable
          title="Non-Brand Query Performansları"
          subtitle="Önceki Yıl Kıyaslamalı"
          keyLabel="Query"
          state={nonBrandQueriesYoY}
          loading={tablesLoading}
          onPage={(p) => pageTable('/queries', setNonBrandQueriesYoY, { group: 'NON_BRAND', compare: 'yoy' }, p)}
        />
      </div>

      {/* Country + device */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        <Card title="Ülke Kırılımı" subtitle={rangeLabel}>
          <div className="overflow-x-auto overflow-y-auto" style={scrollBoxStyle}>
            <table className="min-w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left p-2 sticky top-0 z-10 bg-gray-50">Ülke</th>
                  <th className="text-right p-2 sticky top-0 z-10 bg-gray-50">Clicks</th>
                  <th className="text-right p-2 sticky top-0 z-10 bg-gray-50">% Δ</th>
                  <th className="text-right p-2 sticky top-0 z-10 bg-gray-50">Impressions</th>
                  <th className="text-right p-2 sticky top-0 z-10 bg-gray-50">% Δ</th>
                  <th className="text-right p-2 sticky top-0 z-10 bg-gray-50">CTR</th>
                  <th className="text-right p-2 sticky top-0 z-10 bg-gray-50">Poz.</th>
                </tr>
              </thead>
              <tbody>
                {(countries?.rows || []).map((r, i) => (
                  <tr key={r.key} className={i % 2 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="p-2">{countryLabel(r.key)}</td>
                    <td className="p-2 text-right tabular-nums">{nf(r.clicks)}</td>
                    <td className="p-2 text-right"><Delta value={r.clicksDelta} /></td>
                    <td className="p-2 text-right tabular-nums">{nf(r.impressions)}</td>
                    <td className="p-2 text-right"><Delta value={r.impressionsDelta} /></td>
                    <td className="p-2 text-right tabular-nums">{pct(r.ctr)}</td>
                    <td className="p-2 text-right tabular-nums">{pos(r.position)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card title="Cihaz Kırılımı" subtitle={rangeLabel}>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-2">Cihaz</th>
                  <th className="text-right p-2">Clicks</th>
                  <th className="text-right p-2">% Δ</th>
                  <th className="text-right p-2">Impressions</th>
                  <th className="text-right p-2">% Δ</th>
                  <th className="text-right p-2">CTR</th>
                  <th className="text-right p-2">Poz.</th>
                </tr>
              </thead>
              <tbody>
                {(devices?.rows || []).map((r, i) => (
                  <tr key={r.key} className={i % 2 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="p-2">{r.key}</td>
                    <td className="p-2 text-right tabular-nums">{nf(r.clicks)}</td>
                    <td className="p-2 text-right"><Delta value={r.clicksDelta} /></td>
                    <td className="p-2 text-right tabular-nums">{nf(r.impressions)}</td>
                    <td className="p-2 text-right"><Delta value={r.impressionsDelta} /></td>
                    <td className="p-2 text-right tabular-nums">{pct(r.ctr)}</td>
                    <td className="p-2 text-right tabular-nums">{pos(r.position)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Monthly history */}
      <Card title="Aylık Performans" subtitle="Tüm geçmiş">
        <div className="overflow-x-auto overflow-y-auto" style={scrollBoxStyle}>
          <table className="min-w-full text-sm">
            <thead>
              <tr>
                <th className="text-left p-2 sticky top-0 z-10 bg-gray-50">Ay</th>
                <th className="text-right p-2 sticky top-0 z-10 bg-gray-50">Clicks</th>
                <th className="text-right p-2 sticky top-0 z-10 bg-gray-50">Impressions</th>
                <th className="text-right p-2 sticky top-0 z-10 bg-gray-50">CTR</th>
                <th className="text-right p-2 sticky top-0 z-10 bg-gray-50">Ort. Poz.</th>
                <th className="text-right p-2 sticky top-0 z-10 bg-gray-50">Δ Clicks</th>
                <th className="text-right p-2 sticky top-0 z-10 bg-gray-50">Δ Impr.</th>
                <th className="text-right p-2 sticky top-0 z-10 bg-gray-50">Δ CTR</th>
                <th className="text-right p-2 sticky top-0 z-10 bg-gray-50">Δ Poz.</th>
                <th className="text-right p-2 sticky top-0 z-10 bg-gray-50">YoY Clicks</th>
                <th className="text-right p-2 sticky top-0 z-10 bg-gray-50">YoY Impr.</th>
              </tr>
            </thead>
            <tbody>
              {monthly.map((m, i) => (
                <tr key={m.month} className={i % 2 ? 'bg-gray-50' : 'bg-white'}>
                  <td className="p-2">{fmtMonthLabel(m.month)}</td>
                  <td className="p-2 text-right tabular-nums">{nf(m.clicks)}</td>
                  <td className="p-2 text-right tabular-nums">{nf(m.impressions)}</td>
                  <td className="p-2 text-right tabular-nums">{pct(m.ctr)}</td>
                  <td className="p-2 text-right tabular-nums">{pos(m.position)}</td>
                  <td className="p-2 text-right"><Delta value={m.clicksDelta} /></td>
                  <td className="p-2 text-right"><Delta value={m.impressionsDelta} /></td>
                  <td className="p-2 text-right"><Delta value={m.ctrDelta} /></td>
                  <td className="p-2 text-right"><Delta value={m.positionDelta} invert suffix="" digits={2} /></td>
                  <td className="p-2 text-right"><Delta value={m.yoyClicksDelta} /></td>
                  <td className="p-2 text-right"><Delta value={m.yoyImpressionsDelta} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Keyword × month position grid */}
      <MonthlyPositionTable
        state={monthlyPos}
        loading={monthlyPosLoading}
        search={monthlyPosSearch}
        onSearch={setMonthlyPosSearch}
        onPage={(p) => loadMonthlyPos(p, monthlyPosSearch)}
      />

      {/* URL-section (blog) monthly performance */}
      <PageSectionMonthly
        state={section}
        loading={sectionLoading}
        contains={sectionContains}
        onContains={setSectionContains}
        metric={sectionMetric}
        onMetric={setSectionMetric}
        onPage={(p) => loadSection(p, sectionContains, sectionMetric)}
      />
    </div>
  );
}
